/* eslint-disable-next-line no-unused-vars */
import React, { useRef } from 'react'
import { useIntl } from 'react-intl'
import {
  Button,
  toastShow,
  toastDismiss,
  TOAST_VARIANT_WARNING
} from 'mailup-web-components'
import styled from '@emotion/styled'

/**
 * Parse beePlugin response json looking for dynamic tags
 * @param {Object} response beePlugin response json
 * @param {Object} options additional settings
 * @param {RegExp} options.regex regex to identify tags
 * @param {Function} options.ignore given a string, return true to ignore
 *  when filtering tags
 * @param {Function} options.deny given a string, return true to deny
 *  when filtering tags
 */
export const checkForMergeTags = (response, options) => {
  const optionsDefault = {
    regex: /(?:)[^[\]]+?(?=\])/g,
    ignore: () => false,
    deny: () => false
  }

  const settings = Object.assign({}, optionsDefault, options)
  const ignored = new Set()
  const valid = new Set()
  const denied = new Set()
  let found = false
  try {
    found = response.value.match(settings.regex)
  } catch (e) {
    found = false
  }

  if (found) {
    found
      .filter(tag => {
        if (settings.ignore(tag)) {
          ignored.add(tag)
          return false
        }
        return true
      })
      .filter(tag => {
        if (settings.deny(tag)) {
          denied.add(tag)
          return false
        }
        return true
      })
      .forEach(tag => valid.add(tag))
  }

  return {
    found: found || [],
    denied: [...denied.values()],
    ignored: [...ignored.values()],
    valid: [...valid.values()]
  }
}

/**
 * Abstract interface to locally manage mergeTags warnings
 * @namespace mergeTagsWarning
 * @param {Object} settings settings
 * @param {Array} settings.allowed initial set of allowed tags that won't be returned
 * @param {Function} settings.onWarn called when a tag is added to warning
 * @param {Function} settings.onDismiss called when a tag is removed from warning
 * @param {Function} settings.onIgnore called when a tag is added to ignored
 */
export const mergeTagsWarning = ({ allowed, onWarn, onDismiss, onIgnore }) => {
  const _allowed = new Set([].concat(allowed))
  const _warning = new Set()
  const _ignored = new Set()
  const _used = new Set()

  const o = {
    get allowed() {
      return _allowed
    },
    set allowed(values) {
      _allowed.clear()
      ;[].concat(values || []).forEach(tag => {
        _allowed.add(tag)
      })
    },
    get used() {
      return _used
    },
    use: tags => {
      _used.clear()
      ;[].concat(tags).forEach(tag => _used.add(tag))
      return o
    },
    update,
    ignore,
    dismiss
  }

  return o

  function _clean() {
    const obsolete = [..._warning].reduce(
      (r, tag) => (!_used.has(tag) || _ignored.has(tag) ? [...r, tag] : r),
      []
    )
    if (obsolete.length) {
      obsolete.forEach(tag => {
        dismiss(tag)
      })
    }
  }

  /**
   * Triggers necessary warnings
   * @memberof mergeTagsWarning
   * @instance
   */
  function update() {
    _clean()
    if ([..._used].some(tag => !_allowed.has(tag))) {
      _used.forEach(tag => {
        if (!_allowed.has(tag) && !_warning.has(tag) && !_ignored.has(tag)) {
          _warning.add(tag)
          if (typeof onWarn === 'function') {
            onWarn(tag)
          }
        }
      })
    }
    return o
  }

  /**
   * Ignore a tag, will not be warned anymore
   * @param {String} tag tag to ignore
   * @memberof mergeTagsWarning
   * @instance
   */
  function ignore(tag) {
    _ignored.add(tag)
    if (typeof onIgnore === 'function') {
      onIgnore(tag)
    }
    return o
  }

  /**
   * Dismiss warning for a specific tag, this will be prompted again
   * if used at next update()
   * @param {String} tag tag to dismiss
   * @memberof mergeTagsWarning
   * @instance
   */
  function dismiss(tag) {
    _warning.delete(tag)
    if (typeof onIgnore === 'function') {
      onDismiss(tag)
    }
    return o
  }
}

/**
 * React hook to use mergeTagsWarning interface in components.
 * Shows mailup-web-components <Toast /> when prompting a warn
 * @param {Function} callbacks.ignore side effect passed as onIgnore callback
 * to interface
 * @param {Function} callbacks.dismiss side effect passed as onDismiss callback
 * to interface
 */
export const useMergeTagsToast = function(callbacks) {
  const intl = useIntl()
  const controller = useRef(
    mergeTagsWarning({
      onIgnore: tag => {
        if (typeof callbacks.ignore === 'function') {
          callbacks.ignore(tag)
        }
      },
      onDismiss: tag => {
        toastDismiss(`editor-dynamic-tag-${tag}`)
        if (typeof callbacks.dismiss === 'function') {
          callbacks.dismiss()
        }
      },
      onWarn: tag => {
        toastShow(
          <ToastContent>
            <p>
              {intl.formatMessage(
                { id: 'Load.Bee.AlertDynamicField' },
                {
                  linebreak: <br />,
                  x: <strong>[{tag}]</strong>
                }
              )}
            </p>
            <Button
              kind='negative'
              onClick={() => {
                controller.current.dismiss(tag)
              }}
            >
              {intl.formatMessage({ id: 'Load.Bee.AlertDynamicFieldBtnOk' })}
            </Button>
            <Button
              kind='negative'
              onClick={() => {
                controller.current.ignore(tag).update()
              }}
            >
              {intl.formatMessage({
                id: 'Load.Bee.AlertDynamicFieldBtnAddWhiteList'
              })}
            </Button>
          </ToastContent>,
          TOAST_VARIANT_WARNING,
          `editor-dynamic-tag-${tag}`,
          false,
          false
        )
      }
    })
  )
  return controller.current
}

const ToastContent = styled.div`
  .e-button {
    float: none;
    margin-top: 10px;
  }
`
